import { useMutation, useQueryClient } from 'react-query'
import { message, UploadFile } from 'antd'
import { postJSON } from 'common/services/api'
import { UpdateReferralInput, Referral, REFERRALS_CATEGORY_ID } from '../types'
import { uploadDocuments } from '../../../../../common/utils/documentUpload'

interface UseReferralUpdateReturn {
  updateReferral: (input: UpdateReferralInput, fileList?: UploadFile[]) => Promise<Referral>
  isUpdating: boolean
}

export const useReferralUpdate = (
  residentId: string,
  onSuccess?: () => void
): UseReferralUpdateReturn => {
  const queryClient = useQueryClient()

  const mutation = useMutation(
    async ({ input, fileList }: { input: UpdateReferralInput; fileList?: UploadFile[] }) => {
      const { _id, ...updateData } = input

      if (!_id) {
        throw new Error('Referral ID is required for update')
      }

      const hasFiles = fileList && fileList.length > 0

      let attachmentIds: string[] | undefined = undefined

      // Upload new documents using presign/commit flow if files are provided
      if (hasFiles) {
        try {
          attachmentIds = await uploadDocuments(
            fileList,
            residentId,
            REFERRALS_CATEGORY_ID
          )
        } catch (error) {
          throw new Error('Failed to upload attachments')
        }
      }

      const formattedUpdateData = {
        ...updateData,
        // Only include attachments field if new files were uploaded (replaces all existing)
        ...(attachmentIds !== undefined && { attachments: attachmentIds })
      }

      const response = await postJSON({
        url: `/api/list/referrals/${_id}`,
        method: 'PUT',
        json: formattedUpdateData
      })

      return response.result as Referral
    },
    {
      onSuccess: () => {
        message.success('Referral updated successfully')
        // Invalidate referrals list to refetch
        queryClient.invalidateQueries(['referrals', residentId])
        if (onSuccess) {
          onSuccess()
        }
      },
      onError: (error: any) => {
        message.error(error?.message || 'Failed to update referral')
      }
    }
  )

  const updateReferral = async (
    input: UpdateReferralInput,
    fileList?: UploadFile[]
  ): Promise<Referral> => {
    return mutation.mutateAsync({ input, fileList })
  }

  return {
    updateReferral,
    isUpdating: mutation.isLoading
  }
}
